import { defineStore } from 'pinia';
import localStore from 'store';

import { dashboardBffClient } from '@/api-client';
import type { SystemInfo, UpgradeInfo } from '@/api-client/api';
import constants from '@/constants';
import { info as logInfo } from '@/logger';

import { useMainStore } from './main';

export interface State {
  systemInfo: SystemInfo | null;
  upgradeInfo: UpgradeInfo | null;
  hostSystemInfo: SystemInfo | null; // primary cluster's system info
}

export const useAppInfoStore = defineStore('appInfo', {
  state: (): State => ({
    systemInfo: null,
    upgradeInfo: null,
    hostSystemInfo: null,
  }),
  getters: {
    systemNamespace: (state) => state.systemInfo?.namespace ?? 'kasten-io',
    isMCPrimary: (state) => {
      const primaryFeats = ['mcv1-primary', 'mc-primary'];
      return state.systemInfo?.features.some((feat) => primaryFeats.includes(feat));
    },
    isMCSecondary: (state) => state.systemInfo?.features.includes('mc-secondary'),
    isMCAdmin: () => {
      const mainStore = useMainStore();
      const isMC = mainStore.userInfo.primary === true;
      const isMCAdmin = mainStore.userInfo.isPrimaryAdmin === true;
      return isMC && isMCAdmin;
    },
    useMCLayout: (state) => {
      return (
        localStore.get(constants.localStorage.IS_MULTICLUSTER) === true ||
        state.hostSystemInfo !== null
      );
    },
    isMCCapable: (state) => state.systemInfo?.capabilities.includes('mc'),
    isFIPS: (state) => state.systemInfo?.capabilities?.includes('fips.strict'),
    isVapEnabled: (state) =>
      state.systemInfo?.capabilities?.includes('vap.kasten.policy.permissions'),
    systemVersion: (state) =>
      state.hostSystemInfo?.version ?? state.systemInfo?.version ?? 'latest',
  },
  actions: {
    async fetchSystemInfo() {
      const { data } = await dashboardBffClient.systemInfo.systemInfo();
      this.systemInfo = data;
      logInfo('System info fetched successfully', data);
      // if this is a primary cluster we set the localStorage value to true
      // and also save a 2nd copy of it as hostSystemInfo
      if (this.isMCPrimary) {
        localStore.set(constants.localStorage.IS_MULTICLUSTER, true);
        this.hostSystemInfo = data;
        return;
      }
      // is this is not a primary and no hostSystemInfo has been saved previously
      // set the localStorage value to false
      if (this.hostSystemInfo === null) {
        localStore.set(constants.localStorage.IS_MULTICLUSTER, false);
      }
    },
    async fetchUpgradeInfo() {
      const { data } = await dashboardBffClient.upgradeInfo.upgradeInfo();
      this.upgradeInfo = data;
    },
  },
});
